import React, { useState } from 'react';
import { PageContainer, ProCard, ProForm, ProFormText, ProFormDigit, ProFormSwitch, ProFormTextArea, ProFormGroup } from '@ant-design/pro-components';
import { Button, Typography, Tag, Space, notification, Spin } from 'antd';
import { CopyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axiosClient from '../api/axiosClient';

const { Title, Paragraph, Text } = Typography;

type GenerateYouTubeMetadataRequest = {
    video_id: string;
    extra_prompt?: string;
    use_frames: boolean;
    num_frames_to_fetch: number;
    max_output_tokens: number;
};

type YouTubeMetadata = {
    title_suggestions: string[];
    description: string;
    tags: string[];
    suggested_playlist?: string;
    original_title?: string;
    channel_name?: string;
    video_url?: string;
    video_published_at?: string;
};

type GenerateYouTubeMetadataResponse = {
    success: boolean;
    message?: string;
    metadata?: YouTubeMetadata;
    error?: string;
};

const YouTubeMetadata: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [metadata, setMetadata] = useState<YouTubeMetadata | null>(null);
    const [responseMessage, setResponseMessage] = useState<string>('');

    const handleSubmit = async (values: GenerateYouTubeMetadataRequest) => {
        try {
            setLoading(true);
            setMetadata(null);
            setResponseMessage('');

            const response = await axiosClient.portal.post<GenerateYouTubeMetadataResponse>(
                '/api/employees/generate_youtube_metadata',
                {
                    video_id: values.video_id,
                    extra_prompt: values.extra_prompt || '',
                    use_frames: values.use_frames,
                    num_frames_to_fetch: values.num_frames_to_fetch,
                    max_output_tokens: values.max_output_tokens
                }
            );

            if (response.data.success && response.data.metadata) {
                setMetadata(response.data.metadata);
                setResponseMessage(response.data.message || 'Metadata generated successfully!');
                notification.success({
                    message: 'Success',
                    description: response.data.message || 'YouTube metadata generated successfully!'
                });
            } else {
                notification.error({
                    message: 'Generation Failed',
                    description: response.data.error || response.data.message || 'Failed to generate metadata'
                });
                setResponseMessage(response.data.error || response.data.message || '');
            }
        } catch (error: any) {
            console.error('Error generating metadata:', error);
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || error.response?.data?.message || 'Failed to generate YouTube metadata'
            });
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            notification.success({
                message: 'Copied!',
                description: `${label} copied to clipboard`,
                duration: 2
            });
        });
    };

    return (
        <PageContainer
            title="YouTube Metadata Generator"
            subTitle="Generate optimized metadata for YouTube videos using AI">
            <ProCard>
                <ProForm<GenerateYouTubeMetadataRequest>
                    layout="horizontal"
                    grid={true}
                    rowProps={{ gutter: 20 }}
                    onFinish={handleSubmit}
                    initialValues={{
                        use_frames: true,
                        num_frames_to_fetch: 4,
                        max_output_tokens: 3000
                    }}
                    submitter={{
                        searchConfig: {
                            submitText: 'Generate Metadata'
                        },
                        resetButtonProps: {
                            style: { display: 'none' }
                        },
                        submitButtonProps: {
                            loading: loading,
                            size: 'large'
                        }
                    }}>
                    <ProFormText
                        name="video_id"
                        label="YouTube Video ID"
                        placeholder="e.g., dQw4w9WgXcQ"
                        colProps={{ span: 6 }}
                        rules={[
                            { required: true, message: 'Please enter a YouTube video ID' },
                            { 
                                pattern: /^[a-zA-Z0-9_-]{11}$/, 
                                message: 'Please enter a valid YouTube video ID (11 characters)' 
                            }
                        ]}
                        tooltip="The 11-character ID from the YouTube URL (e.g., youtube.com/watch?v=VIDEO_ID)"
                    />

                    <ProFormSwitch
                        name="use_frames"
                        label="Use Video Frames"
                        colProps={{ span: 5 }}
                        tooltip="Enable to analyze video frames for better metadata generation"
                        fieldProps={{
                            checkedChildren: "Yes",
                            unCheckedChildren: "No"
                        }}
                    />

                    <ProFormDigit
                        name="num_frames_to_fetch"
                        label="Number of Frames to Fetch"
                        placeholder="4"
                        colProps={{ span: 6 }}
                        min={1}
                        max={10}
                        rules={[
                            { required: true, message: 'Please enter number of frames' }
                        ]}
                        tooltip="How many frames to analyze from the video (1-10)
                                !Remind: Higher numbers may improve accuracy but take longer to process
                                and cost more."
                    />

                    <ProFormDigit
                        name="max_output_tokens"
                        label="Max Output Tokens"
                        placeholder="3000"
                        colProps={{ span: 5 }}
                        min={500}
                        max={8000}
                        rules={[
                            { required: true, message: 'Please enter max output tokens' }
                        ]}
                        tooltip="Maximum number of tokens for the AI response (500-8000). Higher values allow longer descriptions but cost more."
                    />

                    <ProFormTextArea
                        name="extra_prompt"
                        label="Extra Instructions (Optional)"
                        placeholder="Add any specific instructions or rules for the AI agent..."
                        colProps={{ span: 24 }}
                        tooltip="Provide additional context or specific requirements for metadata generation"
                        fieldProps={{
                            rows: 4,
                            maxLength: 1000,
                            showCount: true
                        }}
                    />
                </ProForm>
            </ProCard>

            {loading && (
                <ProCard style={{ marginTop: 24, textAlign: 'center' }}>
                    <Spin size="large" />
                    <Paragraph style={{ marginTop: 16 }}>
                        Generating metadata... This may take a few moments.
                    </Paragraph>
                </ProCard>
            )}

            {metadata && !loading && (
                <ProCard
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <span>Generated Metadata</span>
                        </Space>
                    }
                    style={{ marginTop: 24 }}
                    extra={responseMessage && <Text type="secondary">{responseMessage}</Text>}>
                    
                    {/* Title Suggestions */}
                    <ProCard
                        title="Title Suggestions"
                        type="inner"
                        extra={
                            <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => copyToClipboard(metadata.title_suggestions.join('\n\n'), 'Title Suggestions')}>
                                Copy All
                            </Button>
                        }
                        style={{ marginBottom: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {metadata.title_suggestions.map((title, index) => (
                                <ProCard
                                    key={index}
                                    type="inner"
                                    bordered
                                    size="small"
                                    title={`Option ${index + 1}`}
                                    extra={(
                                        <Button
                                            size="small"
                                            type="text"
                                            icon={<CopyOutlined />}
                                            onClick={() => copyToClipboard(title, `Title ${index + 1}`)}>
                                            Copy
                                        </Button>
                                    )}>
                                    <Text strong style={{ fontSize: 15 }}>
                                        {title}
                                    </Text>
                                </ProCard>
                            ))}
                        </Space>
                    </ProCard>

                    {/* Description */}
                    <ProCard
                        title="Description"
                        type="inner"
                        extra={
                            <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => copyToClipboard(metadata.description, 'Description')}>
                                Copy
                            </Button>
                        }
                        style={{ marginBottom: 16 }}>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                            {metadata.description}
                        </Paragraph>
                    </ProCard>

                    {/* Tags */}
                    <ProCard
                        title="Tags"
                        type="inner"
                        extra={
                            <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => copyToClipboard(metadata.tags.join(', '), 'Tags')}>
                                Copy
                            </Button>
                        }
                        style={{ marginBottom: 16 }}>
                        <Space size={[8, 8]} wrap>
                            {metadata.tags.map((tag, index) => (
                                <Tag key={index} color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                                    {tag}
                                </Tag>
                            ))}
                        </Space>
                        <Paragraph style={{ marginTop: 16, marginBottom: 0 }}>
                            <Text type="secondary">
                                {metadata.tags.length} tags • {metadata.tags.join(', ').length} characters
                            </Text>
                        </Paragraph>
                    </ProCard>

                    {/* Suggested Playlist */}
                    {metadata.suggested_playlist && (
                        <ProCard
                            title="Suggested Playlist"
                            type="inner"
                            extra={
                                <Button
                                    type="text"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(metadata.suggested_playlist!, 'Suggested Playlist')}>
                                    Copy
                                </Button>
                            }>
                            <Text strong style={{ fontSize: 16 }}>
                                {metadata.suggested_playlist}
                            </Text>
                        </ProCard>
                    )}
                </ProCard>
            )}
        </PageContainer>
    );
};

export default YouTubeMetadata;
